import { ApiQuest, ApiQuestBoost, ApiQuestBoostPayload } from "griptonite-api-client/types";
import { z } from "zod";

// Python's weekday(), as the API uses: Monday is 0.
export const WEEKDAYS = [
  { value: 0, name: "Mon" },
  { value: 1, name: "Tue" },
  { value: 2, name: "Wed" },
  { value: 3, name: "Thu" },
  { value: 4, name: "Fri" },
  { value: 5, name: "Sat" },
  { value: 6, name: "Sun" },
];

// A FIXED boost is one absolute window; a PERIODIC one repeats weekly between two local times,
// and its dates, if any, just bound when that repeating happens. So both kinds share
// startTime/endTime, and only a FIXED boost has to have them.
const BoostFormBaseSchema = z.object({
  uuid: z.string().optional(),
  name: z.string().trim(),
  type: z.enum(["FIXED", "PERIODIC"]),
  multiplier: z
    .number({ message: "Multiplier required" })
    .gt(1, { message: "Must be greater than 1" })
    .max(5, { message: "At most 5" }),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  daysOfWeek: z.array(z.number()),
  localStartTime: z.string().nullable(),
  localEndTime: z.string().nullable(),
});

export type BoostFormValues = z.infer<typeof BoostFormBaseSchema>;

// The API refuses a boost that sticks out of the quest's own window, so the form says so first.
// A quest with no start, or no end, is open on that side.
export const makeBoostFormSchema = (quest: Pick<ApiQuest, "startTime" | "endTime">) =>
  BoostFormBaseSchema.superRefine((values, ctx) => {
    const issue = (path: keyof BoostFormValues, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });

    const start = values.startTime ? new Date(values.startTime).getTime() : null;
    const end = values.endTime ? new Date(values.endTime).getTime() : null;

    if (values.type === "FIXED") {
      if (start === null) issue("startTime", "Start time required");
      if (end === null) issue("endTime", "End time required");
    } else {
      if (values.daysOfWeek.length === 0) issue("daysOfWeek", "Choose at least one day");
      if (!values.localStartTime) issue("localStartTime", "Start time required");
      if (!values.localEndTime) issue("localEndTime", "End time required");
      if (
        values.localStartTime &&
        values.localEndTime &&
        values.localStartTime >= values.localEndTime
      ) {
        issue("localEndTime", "Must be after the start time");
      }
    }

    if (start !== null && end !== null && start >= end) {
      issue("endTime", "Must be after the start time");
    }

    const questStart = quest.startTime ? new Date(quest.startTime).getTime() : null;
    const questEnd = quest.endTime ? new Date(quest.endTime).getTime() : null;
    const outside = (time: number | null) =>
      time !== null &&
      ((questStart !== null && time < questStart) || (questEnd !== null && time > questEnd));
    if (outside(start)) issue("startTime", "Must be within the quest's start and end");
    if (outside(end)) issue("endTime", "Must be within the quest's start and end");
  });

export const boostToFormValues = (boost?: ApiQuestBoost): BoostFormValues => ({
  uuid: boost?.uuid,
  name: boost?.name ?? "",
  type: boost?.type ?? "FIXED",
  multiplier: boost?.multiplier ?? (null as unknown as number),
  startTime: boost?.startTime ?? null,
  endTime: boost?.endTime ?? null,
  daysOfWeek: boost?.daysOfWeek ?? [],
  localStartTime: boost?.localStartTime?.slice(0, 5) ?? null,
  localEndTime: boost?.localEndTime?.slice(0, 5) ?? null,
});

export const boostFormValuesToPayload = (values: BoostFormValues): ApiQuestBoostPayload => ({
  name: values.name,
  type: values.type,
  multiplier: values.multiplier,
  startTime: values.startTime,
  endTime: values.endTime,
  // A FIXED boost has no weekly part; the API clears it anyway, but this keeps the intent plain.
  daysOfWeek: values.type === "PERIODIC" ? [...values.daysOfWeek].sort() : null,
  localStartTime: values.type === "PERIODIC" ? values.localStartTime : null,
  localEndTime: values.type === "PERIODIC" ? values.localEndTime : null,
});
